import { z } from 'zod';
import LangEnum from '../../lang/LangEnum';
import { TranslateType } from '../../modules/translation/translationContext.types';
import { yupValidateZipCode } from '../../utils/validators';
import { AddressTypeEnum } from './adress.enum';

const MAX_LENGTH = 200;

export const buildAddressSchema = (translate: TranslateType) =>
  z.object({
    zipCode: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .refine(yupValidateZipCode, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_CEP)),
    streetDescription: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_CITY_DESCRIPTION_LENGTH)),
    streetNumber: z.string().trim().min(1, translate(LangEnum.GLOBAL_REQUIRED)),
    complement: z
      .string()
      .trim()
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_COMPLEMENT_LENGTH)),
    neighborhood: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_NEIGHBORHOOD_LENGTH)),
    city: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_CITY_LENGTH)),
    state: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_STATE_LENGTH)),
    country: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_COUNTRY_LENGTH)),
    code: z
      .string()
      .trim()
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_COMPLEMENT_LENGTH)),
    municipalityCode: z.string().optional(),
    type: z.enum(AddressTypeEnum, translate(LangEnum.GLOBAL_REQUIRED)),
  });

export const buildInternationalAddressSchema = (translate: TranslateType) =>
  z.object({
    zipCode: z.string().trim().min(1, translate(LangEnum.GLOBAL_REQUIRED)),
    streetDescription: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_CITY_DESCRIPTION_LENGTH)),
    streetNumber: z.string().trim(),
    complement: z
      .string()
      .trim()
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_COMPLEMENT_LENGTH)),
    neighborhood: z
      .string()
      .trim()
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_NEIGHBORHOOD_LENGTH)),
    city: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_CITY_LENGTH)),
    state: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_STATE_LENGTH)),
    country: z
      .string()
      .trim()
      .min(1, translate(LangEnum.GLOBAL_REQUIRED))
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_COUNTRY_LENGTH)),
    code: z
      .string()
      .trim()
      .max(MAX_LENGTH, translate(LangEnum.ADDRESS_VALIDATIONS_INVALID_COMPLEMENT_LENGTH)),
    municipalityCode: z.string().optional(),
    type: z.enum(AddressTypeEnum, translate(LangEnum.GLOBAL_REQUIRED)),
  });

interface BuildAddressFormSchemaParams {
  translate: TranslateType;
  isInternational?: boolean;
}

export const buildAddressFormSchema = ({
  translate,
  isInternational,
}: BuildAddressFormSchemaParams) =>
  z.object({
    principal: z.boolean().optional(),
    address: isInternational
      ? buildInternationalAddressSchema(translate)
      : buildAddressSchema(translate),
  });

export type AddressValues = z.infer<ReturnType<typeof buildAddressSchema>>;

export interface AddressFormValues {
  principal?: boolean;
  address: AddressValues;
}
